import { Minus, Maximize2, Minimize2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SessionSelector } from "./SessionSelector";
import type { ChatSessionInstance } from "@/stores/chat/ChatStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useDoubleTap } from "@/hooks/useDoubleTap";

/**
 * Props for the SidebarChatHeader component.
 */
export interface SidebarChatHeaderProps {
  /** Callback to collapse the chat to bar */
  onClose: () => void;
  /** Whether the chat is in expanded/maximized mode (WHA-2390) */
  isExpanded?: boolean;
  /** Callback to expand/maximize the chat window (WHA-2390) */
  onExpand?: () => void;
  /** Callback to restore from expanded to normal size (WHA-2390) */
  onCollapse?: () => void;
  // Multi-session props (WHA-1465)
  /** List of available sessions */
  sessions?: ChatSessionInstance[];
  /** Currently active session ID */
  activeSessionId?: string;
  /** Callback when a session is selected */
  onSessionSelect?: (sessionId: string) => void;
  /** Callback to create a new session */
  onSessionCreate?: () => void;
  /** Callback to archive a session */
  onSessionArchive?: (sessionId: string) => void;
  /** Callback to rename a session */
  onSessionRename: (sessionId: string, title: string) => Promise<void> | void;
  /** Whether sessions are loading */
  sessionsLoading?: boolean;
}

/**
 * SidebarChatHeader - Header component for the sidebar chat.
 *
 * Displays the Franklin logo, live session controls, and window controls.
 * Used as drag handle for the ChatWidget via `chat-drag-handle` className.
 */
export function SidebarChatHeader({
  onClose,
  isExpanded,
  onExpand,
  onCollapse,
  sessions,
  activeSessionId,
  onSessionSelect,
  onSessionCreate,
  onSessionArchive,
  onSessionRename,
  sessionsLoading,
}: SidebarChatHeaderProps) {
  const { t } = useLanguage();
  const doubleTapHandlers = useDoubleTap(onClose);
  const showSessionSelector =
    sessions !== undefined &&
    onSessionSelect !== undefined &&
    onSessionCreate !== undefined &&
    onSessionArchive !== undefined &&
    onSessionRename !== undefined;

  return (
    <header
      className={cn(
        "chat-drag-handle flex cursor-grab items-center gap-3 px-4 py-3.5",
        "rounded-t-[10px] border-b border-[#262626] bg-[#262626]"
      )}
      onDoubleClick={doubleTapHandlers.onDoubleClick}
      onTouchEnd={doubleTapHandlers.onTouchEnd}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <img
          src="/franklin-logo.svg"
          alt="Franklin"
          draggable={false}
          className="h-8 w-8 shrink-0 object-contain"
        />
        {showSessionSelector && (
          <div onMouseDown={(event) => event.stopPropagation()}>
            <SessionSelector
              sessions={sessions}
              activeSessionId={activeSessionId}
              onSessionSelect={onSessionSelect}
              onSessionCreate={onSessionCreate}
              onSessionArchive={onSessionArchive}
              onSessionRename={onSessionRename}
              isLoading={sessionsLoading ?? false}
              showCreateAction={false}
              iconTrigger
              inverseTrigger
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {onSessionCreate && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onSessionCreate}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={sessionsLoading}
            aria-label={t.documents.chatSidebar.newChat}
            title={t.documents.chatSidebar.newChat}
            className="h-8 w-8 text-neutral-100 hover:bg-white/10 hover:text-white focus-visible:ring-white focus-visible:ring-offset-[#262626]"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
        {onExpand && onCollapse && (
          <Button
            variant="ghost"
            size="icon"
            onClick={isExpanded ? onCollapse : onExpand}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={
              isExpanded
                ? t.documents.chatSidebar.collapseToSidebar
                : t.documents.chatSidebar.expandChat
            }
            title={
              isExpanded
                ? t.documents.chatSidebar.collapseToSidebar
                : t.documents.chatSidebar.expandChat
            }
            className="h-8 w-8 text-neutral-100 hover:bg-white/10 hover:text-white focus-visible:ring-white focus-visible:ring-offset-[#262626]"
          >
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={t.documents.chatSidebar.closeChat}
          title={t.documents.chatSidebar.minimizeChat}
          className="h-8 w-8 text-neutral-100 hover:bg-white/10 hover:text-white focus-visible:ring-white focus-visible:ring-offset-[#262626]"
        >
          <Minus className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}

SidebarChatHeader.displayName = "SidebarChatHeader";
